import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CreateCampaign from './CreateCampaign';
import './Campaigns.css';

const API_URL = '/api/v2';

function Campaigns({ user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const navigate = useNavigate();

  const handleCreated = (data) => {
    setShowCreate(false);
    setEditCampaign(null);
    fetchCampaigns();
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(API_URL + '/campaigns');
      setCampaigns(res.data.campaigns || []);
      if (res.data.campaigns?.length) selectCampaign(res.data.campaigns[0].slug);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const selectCampaign = async (slug) => {
    try {
      setLoading(true);
      const [campRes, statsRes, patsRes, uploadsRes] = await Promise.all([
        axios.get(API_URL + '/campaigns/' + slug),
        axios.get(API_URL + '/campaigns/' + slug + '/stats'),
        axios.get(API_URL + '/campaigns/' + slug + '/patients?limit=20'),
        axios.get(API_URL + '/campaigns/' + slug + '/uploads').catch(() => ({ data: { uploads: [] } }))
      ]);
      setSelectedCampaign(campRes.data);
      setStats(statsRes.data);
      setPatients(patsRes.data.patients || []);
      setUploads(uploadsRes.data.uploads || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeleteUpload = async (uploadId, filename) => {
    if (!window.confirm('Delete all patients from upload "' + filename + '"? This cannot be undone.')) return;
    setDeleting(uploadId);
    try {
      await axios.delete(API_URL + '/uploads/' + uploadId);
      selectCampaign(selectedCampaign.slug);
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setDeleting(null); }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    const name = selectedCampaign.name;
    const patientCount = stats?.total_patients || 0;
    const msg = patientCount > 0
      ? 'DELETE "' + name + '"?\n\nThis will permanently delete ' + patientCount + ' patient records, all study arms, and all data fields.\n\nType the campaign name to confirm:'
      : 'Delete campaign "' + name + '"? This cannot be undone.';
    if (patientCount > 0) {
      const typed = window.prompt(msg);
      if (!typed || typed.trim().toLowerCase() !== name.trim().toLowerCase()) {
        if (typed !== null) alert('Campaign name did not match. Deletion cancelled.');
        return;
      }
    } else {
      if (!window.confirm(msg)) return;
    }
    try {
      await axios.delete(API_URL + '/campaigns/' + selectedCampaign.slug);
      setSelectedCampaign(null);
      setStats(null);
      setPatients([]);
      fetchCampaigns();
    } catch (err) {
      alert('Error deleting campaign: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditCampaign = () => {
    if (!selectedCampaign) return;
    setEditCampaign(selectedCampaign);
  };

  const exportCSV = async (slug) => {
    window.open('/api/v2/campaigns/' + slug + '/export?format=csv', '_blank');
  };

  if (loading && !selectedCampaign) return <div className="loading-spinner"><div className="spinner"></div><p>Loading campaigns...</p></div>;

  return (
    <div className="campaigns-container">
      <div className="campaigns-header">
        <h1>Research Campaigns</h1>
        <button className="btn-create-campaign" onClick={() => setShowCreate(true)}>+ New Campaign</button>
        <p className="subtitle">Collaborative spine surgery studies</p>
      </div>

      <div className="campaign-tabs">
        {campaigns.map(c => (
          <button key={c.slug} className={'tab ' + (selectedCampaign?.slug === c.slug ? 'active' : '')}
            onClick={() => selectCampaign(c.slug)}>
            <span className="tab-name">{c.name.length > 35 ? c.name.slice(0,35)+'...' : c.name}</span>
            <span className="tab-count">{c.patient_count} patients</span>
          </button>
        ))}
      </div>

      {selectedCampaign && stats && (
        <>
          <div className="campaign-info">
            <div className="info-main">
              <h2>{selectedCampaign.name}</h2>
              <p className="description">{selectedCampaign.description}</p>
              <div className="meta-row">
                {selectedCampaign.pi_name && <span className="meta-tag">PI: {selectedCampaign.pi_name}</span>}
                <span className="meta-tag">{selectedCampaign.study_type}</span>
                <span className="meta-tag status-active">{selectedCampaign.status}</span>
                {selectedCampaign.primary_outcome && <span className="meta-tag outcome">Primary: {selectedCampaign.primary_outcome}</span>}
              </div>
            </div>
            <div className="info-actions">
              <button className="btn-edit" onClick={handleEditCampaign}>✏️ Edit</button>
              <button className="btn-export" onClick={() => exportCSV(selectedCampaign.slug)}>Export CSV</button>
              {(user?.role === 'admin' || (user?.id && selectedCampaign.created_by === user.id)) && (
              <button className="btn-delete-campaign" onClick={handleDeleteCampaign}>🗑️</button>
              )}
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-value">{stats.total_patients}</div>
              <div className="stat-label">Total Patients</div>
            </div>
            <div className="stat-card arms">
              <div className="stat-value">{stats.by_arm?.length || 0}</div>
              <div className="stat-label">Study Arms</div>
            </div>
            <div className="stat-card completeness">
              <div className="stat-value">{Math.round(stats.avg_completeness * 100)}%</div>
              <div className="stat-label">Avg Completeness</div>
            </div>
            <div className="stat-card fields">
              <div className="stat-value">{selectedCampaign.fields?.length || 0}</div>
              <div className="stat-label">Data Fields</div>
            </div>
          </div>

          {stats.by_arm && stats.by_arm.length > 0 && (
            <div className="arms-section">
              <h3>Study Arms</h3>
              <div className="arms-grid">
                {stats.by_arm.map(arm => {
                  const pct = stats.total_patients > 0 ? Math.round((parseInt(arm.count) / stats.total_patients) * 100) : 0;
                  return (
                    <div key={arm.slug} className="arm-card">
                      <div className="arm-name">{arm.name}</div>
                      <div className="arm-count">{arm.count} patients</div>
                      <div className="arm-bar"><div className="arm-fill" style={{width: pct + '%'}}></div></div>
                      <div className="arm-pct">{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {uploads.length > 0 && (
            <div className="uploads-section">
              <h3>Upload History <span className="field-count">{uploads.length} imports</span></h3>
              <div className="uploads-list">
                {uploads.map(u => (
                  <div key={u.id} className="upload-row">
                    <span className="ul-icon">📄</span>
                    <div className="ul-info">
                      <span className="ul-name">{u.filename}</span>
                      <span className="ul-meta">{u.current_count || u.inserted_count} patients · {new Date(u.created_at).toLocaleDateString()} {u.uploaded_by_name ? '· by ' + u.uploaded_by_name : ''}</span>
                    </div>
                    {(user?.role === 'admin' || (user?.id && u.uploaded_by === user.id)) && (
                    <button className="btn-delete-upload" onClick={() => handleDeleteUpload(u.id, u.filename)} disabled={deleting === u.id}>
                      {deleting === u.id ? '...' : '🗑'}
                    </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCampaign.fields && selectedCampaign.fields.length > 0 && (
            <div className="schema-section">
              <h3>Data Schema <span className="field-count">{selectedCampaign.fields.length} fields</span></h3>
              <div className="schema-categories">
                {[...new Set(selectedCampaign.fields.map(f => f.category))].map(cat => (
                  <div key={cat} className="schema-category">
                    <h4>{cat.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}</h4>
                    <div className="field-list">
                      {selectedCampaign.fields.filter(f => f.category === cat).map(f => (
                        <div key={f.field_name} className={'field-item ' + (f.is_required ? 'required' : '')}>
                          <span className="field-name">{f.field_label}</span>
                          <span className="field-type">{f.data_type}</span>
                          {f.is_required && <span className="field-req">required</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {patients.length > 0 && (
            <div className="patients-section">
              <h3>Recent Patients <span className="field-count">{stats.total_patients} total</span></h3>
              <div className="table-wrapper">
                <table className="patients-table">
                  <thead>
                    <tr>
                      <th>Arm</th>
                      <th>Sex</th>
                      <th>Age</th>
                      <th>BMI</th>
                      <th>Pre-op ODI</th>
                      <th>Pre-op VAS</th>
                      <th>Completeness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.map(p => (
                      <tr key={p.id}>
                        <td><span className="arm-badge">{p.arm_name || '-'}</span></td>
                        <td>{p.data?.sex || '-'}</td>
                        <td>{p.data?.age_at_surgery || '-'}</td>
                        <td>{p.data?.bmi || '-'}</td>
                        <td>{p.data?.preop_odi || '-'}</td>
                        <td>{p.data?.preop_vas_back || '-'}</td>
                        <td>
                          <div className="completeness-bar">
                            <div className="completeness-fill" style={{width: Math.round(p.completeness * 100) + '%'}}></div>
                            <span>{Math.round(p.completeness * 100)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {showCreate && <CreateCampaign onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {editCampaign && <CreateCampaign onClose={() => setEditCampaign(null)} onCreated={handleCreated} editData={editCampaign} />}
    </div>
  );
}
export default Campaigns;
